/**
 * Thin fetch wrapper for the Accounts backend. Returns parsed JSON or
 * throws an `ApiError` on non-2xx responses. React Query turns these into
 * retry-or-surface decisions.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: { Accept: 'application/json' },
    credentials: 'include', // send the signed session cookie on every call
  };
  if (body !== undefined) {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(path, init);
  const contentType = res.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json')
    ? ((await res.json()) as unknown)
    : await res.text();
  if (!res.ok) {
    const message =
      typeof parsed === 'object' && parsed !== null && 'message' in parsed
        ? String((parsed as { message: unknown }).message)
        : `${method} ${path} failed with ${res.status}`;
    throw new ApiError(res.status, message, parsed);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

// ---- Types mirroring the backend ---------------------------------------

export type PartyRole = 'self' | 'upline' | 'client';

export interface Party {
  id: number;
  role: PartyRole;
  displayName: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Account {
  id: number;
  uplinePartyId: number;
  canonicalName: string;
  panel: string | null;
  currencyLabel: string;
  creditLimitLabel: string | null;
  status: 'active' | 'inactive' | 'closed';
  /** true = client rate mirrors upline rate; false = separate client rate. */
  equalSpread: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountAlias {
  id: number;
  accountId: number | null;
  rawName: string;
  ignored: boolean;
  ignoredReason: string | null;
}

export interface Assignment {
  id: number;
  accountId: number;
  clientPartyId: number | null;
  clientRate: string | null;
  uplineRate: string;
  defaultPointsSplit: boolean;
  effectiveFrom: string;
  effectiveTo: string | null;
  notes: string | null;
}

export interface AccountAssignments {
  assignments: Assignment[];
  activeAssignment: Assignment | null;
}

export interface RateChangeResponse {
  account: Account;
  assignment: Assignment;
}

export interface OpeningBalance {
  id: number;
  partyId: number;
  asOfDate: string;
  amount: string;
  note: string;
  source: string;
  createdAt: string;
}

export type WizardStep =
  | 'self'
  | 'uplines'
  | 'clients'
  | 'accounts'
  | 'opening_balances'
  | 'done';

export interface WizardState {
  completed: boolean;
  currentStep: WizardStep;
  stepStatus: Record<WizardStep, 'complete' | 'pending'>;
  counts: {
    selfParties: number;
    uplineParties: number;
    clientParties: number;
    accounts: number;
    assignments: number;
    aliases: number;
    openingBalances: number;
  };
}

export type BatchState = 'draft' | 'verified' | 'archived';

export interface WeeklyBatch {
  id: number;
  uplinePartyId: number;
  weekMonday: string;
  state: BatchState;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  verifiedAt: string | null;
  archivedAt: string | null;
}

export interface RawUplineRow {
  id: number;
  batchId: number;
  panel: string | null;
  rawAccountName: string;
  currencyLabelStated: string | null;
  creditLimitStated: string | null;
  pointsStated: string;
  rateStated: string;
  amountStated: string;
  flags: string[];
  sourceAttachmentId: number | null;
  sourceRowIndex: number | null;
  matchedAccountId: number | null;
  ignored: boolean;
  createdAt: string;
}

export type VarianceSeverity = 'NONE' | 'MINOR' | 'REVIEW' | 'MAJOR';
export type SettlementState = 'pending_confirmation' | 'computed' | 'locked';

export interface Settlement {
  id: number;
  batchId: number;
  accountId: number;
  assignmentId: number | null;
  rawRowId: number | null;
  clientPartyId: number | null;
  uplinePoints: string;
  clientPoints: string;
  uplineRateUsed: string;
  clientRateUsed: string | null;
  uplineCost: string;
  clientAmount: string | null;
  userMargin: string;
  amountStated: string | null;
  varianceDelta: string | null;
  varianceSeverity: VarianceSeverity;
  state: SettlementState;
  pointsOverridden: boolean;
  computedAt: string;
  lockedAt: string | null;
  notes: string | null;
}

export interface BatchTotals {
  uplineCostSum: string;
  clientAmountSum: string;
  userMarginSum: string;
  rowCount: number;
  majorVarianceCount: number;
  pendingCount: number;
}

export type AdvisoryKind = 'rotation' | 'rate_drift' | 'no_assignment' | 'missing_raw';

export interface SettlementAdvisory {
  settlementId: number;
  kind: AdvisoryKind;
  detail: string;
  previousBatchId?: number | null;
}

export interface BatchAttachment {
  id: number;
  batchId: number;
  sha256: string;
  originalFilename: string;
  mimeType: string;
  byteSize: number;
  uploadedAt: string;
}

export interface VerifyBlocker {
  kind: 'UNMATCHED_ROW' | 'PENDING_CONFIRMATION' | 'MAJOR_VARIANCE';
  rowOrSettlementId: number;
  detail: string;
}

export type RowClassification =
  | { kind: 'panel_header'; panel: string; rowIndex: number }
  | { kind: 'column_header'; rowIndex: number }
  | { kind: 'total'; rowIndex: number }
  | { kind: 'data'; rowIndex: number; panel: string | null }
  | { kind: 'empty'; rowIndex: number };

export interface PanelGroup {
  panel: string | null;
  dataRowIndexes: number[];
}

export interface ParsedSheet {
  name: string;
  rowCount: number;
  columnCount: number;
  rowOffset: number;
  rows: (string | null)[][];
  classifications: RowClassification[];
  panels: PanelGroup[];
}

export interface ParsedWorkbookResponse {
  originalFilename?: string | null;
  sheets: ParsedSheet[];
}

export interface OcrAccountMatch {
  rawName: string;
  accountId: number | null;
  accountLabel: string | null;
  via: 'alias' | 'fuzzy' | null;
}

export interface OcrKnownAccount {
  id: number;
  label: string;
  panel: string | null;
}

export interface OcrClassifyResponse extends ParsedWorkbookResponse {
  accountMatches: OcrAccountMatch[];
  knownAccounts: OcrKnownAccount[];
  /** True when vision-LLM failed and OCR fallback was used. */
  fallback?: boolean;
}

// ---- ledger / payments / adjustments ------------------------------------

export type LedgerEntryType = 'WEEKLY_PNL' | 'PAYMENT' | 'ADJUSTMENT' | 'TRANSFER';

export interface LedgerEntry {
  id: number;
  partyId: number;
  effectiveDate: string;
  type: LedgerEntryType;
  dr: string;
  cr: string;
  description: string;
  sourceSettlementId: number | null;
  sourcePaymentId: number | null;
  sourceAdjustmentId: number | null;
  sourceBatchId: number | null;
  reversesEntryId: number | null;
  postedAt: string;
}

export type PaymentMethod = 'cash' | 'bank_transfer' | 'upi' | 'cheque' | 'other';

export interface Payment {
  id: number;
  effectiveDate: string;
  amount: string;
  method: PaymentMethod;
  payerPartyId: number;
  payeePartyId: number;
  viaSelf: boolean;
  note: string | null;
  voidedAt: string | null;
  voidedReason: string | null;
  createdAt: string;
}

export interface Adjustment {
  id: number;
  effectiveDate: string;
  description: string;
  amount: string;
  drPartyId: number | null;
  crPartyId: number | null;
  note: string | null;
  voidedAt: string | null;
  voidedReason: string | null;
  createdAt: string;
}

export interface LedgerListingRow {
  id: number;
  effectiveDate: string;
  type: LedgerEntryType;
  dr: string;
  cr: string;
  description: string;
  sourceSettlementId: number | null;
  sourcePaymentId: number | null;
  sourceAdjustmentId: number | null;
  sourceBatchId: number | null;
  reversesEntryId: number | null;
  runningBalance: string;
}

export interface LedgerListing {
  partyId: number;
  from: string;
  to: string;
  openingBalanceAsOfFrom: string;
  rows: LedgerListingRow[];
  closingBalance: string;
}

export interface EntryAttachment {
  id: number;
  entryId: number;
  sha256: string;
  originalFilename: string;
  mimeType: string;
  byteSize: number;
  uploadedAt: string;
}

export interface PartyBalance {
  partyId: number;
  asOfDate: string | null;
  balance: string;
}

// ---- Auth -----------------------------------------------------------------

export type UserRole = 'owner' | 'boss';

export interface UserSummary {
  id: number;
  username: string;
  role: UserRole;
  hasPin: boolean;
  createdAt: string;
}

export type WhoamiResponse =
  | { state: 'bootstrap_required' }
  | { state: 'unauthenticated' }
  | {
      state: 'authenticated';
      userId: number;
      username: string;
      role: UserRole;
      hasPin: boolean;
      pinUnlockedUntil: string | null;
    };

// ---- Match-entries module ------------------------------------------------

export type MatchAccountStatus = 'active' | 'archived';

export interface MatchAccount {
  id: number;
  name: string;
  notes: string | null;
  linkedPartyId: number | null;
  status: MatchAccountStatus;
  createdAt: string;
  updatedAt: string;
}

export interface MatchSplit {
  id: number;
  matchAccountId: number;
  targetMatchAccountId: number;
  label: string;
  percentage: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface MatchEntry {
  id: number;
  sourceMatchAccountId: number;
  amount: string;
  effectiveDate: string;
  note: string | null;
  createdByUserId: number;
  hadOverride: boolean;
  voidedAt: string | null;
  voidedReason: string | null;
  createdAt: string;
}

export interface MatchLedgerEntry {
  id: number;
  matchEntryId: number;
  targetMatchAccountId: number;
  amount: string;
  label: string | null;
  percentageUsed: string | null;
  partKind: 'split' | 'remainder';
  reversesEntryId: number | null;
  createdAt: string;
}

export interface MatchAccountDetail {
  account: MatchAccount;
  splits: MatchSplit[];
  balance: string;
}

export interface MatchEntryDetail {
  entry: MatchEntry;
  ledgerEntries: MatchLedgerEntry[];
}

export interface CreateMatchAccountInput {
  name: string;
  notes?: string | null;
  linkedPartyId?: number | null;
}

export interface CreateMatchEntryInput {
  sourceMatchAccountId: number;
  amount: string;
  effectiveDate: string;
  note?: string | null;
  overrideSplits?: Array<{
    targetMatchAccountId: number;
    label: string;
    percentage: string;
  }>;
}

export interface SplitReplaceInput {
  splits: Array<{
    targetMatchAccountId: number;
    label: string;
    percentage: string;
  }>;
}

export interface MatchLedgerRow {
  id: number;
  match_entry_id: number;
  target_match_account_id: number;
  amount: string;
  label: string | null;
  percentage_used: string | null;
  part_kind: 'split' | 'remainder';
  reverses_entry_id: number | null;
  created_at: string;
  effective_date: string;
  source_match_account_id: number;
  voided_at: string | null;
  note: string | null;
}

function buildQuery(
  params: Record<string, string | number | boolean | undefined | null>,
): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  const qs = entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return `?${qs}`;
}

export const match = {
  // ---- Accounts -----------------------------------------------------------
  listAccounts: (params?: { status?: 'active' | 'archived' | 'all' }) =>
    api.get<MatchAccount[]>(
      `/api/match/accounts${buildQuery({ status: params?.status })}`,
    ),
  getAccount: (id: number) =>
    api.get<MatchAccountDetail>(`/api/match/accounts/${id}`),
  createAccount: (input: CreateMatchAccountInput) =>
    api.post<MatchAccount>('/api/match/accounts', input),
  updateAccount: (id: number, input: Partial<CreateMatchAccountInput>) =>
    api.patch<MatchAccount>(`/api/match/accounts/${id}`, input),
  archiveAccount: (id: number) =>
    api.post<MatchAccount>(`/api/match/accounts/${id}/archive`),
  unarchiveAccount: (id: number) =>
    api.post<MatchAccount>(`/api/match/accounts/${id}/unarchive`),

  // ---- Splits -------------------------------------------------------------
  getSplits: (matchAccountId: number) =>
    api.get<MatchSplit[]>(`/api/match/accounts/${matchAccountId}/splits`),
  replaceSplits: (matchAccountId: number, input: SplitReplaceInput) =>
    api.put<MatchSplit[]>(
      `/api/match/accounts/${matchAccountId}/splits`,
      input,
    ),

  // ---- Entries ------------------------------------------------------------
  listEntries: (params?: {
    source?: number;
    from?: string;
    to?: string;
    includeVoided?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<MatchEntry[]>(
      `/api/match/entries${buildQuery({
        source: params?.source,
        from: params?.from,
        to: params?.to,
        includeVoided: params?.includeVoided,
        limit: params?.limit,
        offset: params?.offset,
      })}`,
    ),
  getEntry: (id: number) =>
    api.get<MatchEntryDetail>(`/api/match/entries/${id}`),
  createEntry: (input: CreateMatchEntryInput) =>
    api.post<{ entry: MatchEntry; ledgerEntries: MatchLedgerEntry[] }>(
      '/api/match/entries',
      input,
    ),
  voidEntry: (id: number, reason: string) =>
    api.post<{ entry: MatchEntry; reversalEntries: MatchLedgerEntry[] }>(
      `/api/match/entries/${id}/void`,
      { reason },
    ),

  // ---- Ledger + balances --------------------------------------------------
  getLedger: (
    matchAccountId: number,
    params?: { from?: string; to?: string; limit?: number; offset?: number },
  ) =>
    api.get<{ rows: MatchLedgerRow[] }>(
      `/api/match/accounts/${matchAccountId}/ledger${buildQuery({
        from: params?.from,
        to: params?.to,
        limit: params?.limit,
        offset: params?.offset,
      })}`,
    ),
  getBalance: (matchAccountId: number) =>
    api.get<{ matchAccountId: number; balance: string }>(
      `/api/match/accounts/${matchAccountId}/balance`,
    ),
  listBalances: () =>
    api.get<Array<{ matchAccountId: number; balance: string }>>(
      '/api/match/balances',
    ),
};
